import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Star, ShieldCheck, Download, Gift, Lock } from 'lucide-react';
export function OpeningLead() {
  const scrollToCTA = () => {
    const cta = document.getElementById('cta-section');
    if (cta) {
      cta.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="pt-20 pb-12 md:pb-20 px-4 md:px-6 w-full relative bg-[var(--bg-deep)] overflow-hidden" data-id="element-193">
      {/* Soft warm background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[70vh] pointer-events-none z-0" style={{
      background: 'radial-gradient(ellipse 70% 80% at 50% 0%, var(--accent-soft) 0%, transparent 70%)'
    }} data-id="element-194" />

      <motion.div variants={container} initial="hidden" animate="show" className="max-w-[720px] mx-auto text-center relative z-10 flex flex-col items-center" data-id="element-195">
        {/* Label */}
        <motion.div variants={item} data-id="element-196">
          <p className="text-[var(--accent)] font-bold tracking-widest uppercase text-xs md:text-sm mb-3 md:mb-4 bg-[var(--accent-soft)] px-3 py-1 rounded-full inline-block" data-id="element-197">
            ✨ Sensory Feeding Victory
          </p>
        </motion.div>

        {/* Headline */}
        <motion.div variants={item} data-id="element-198">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold text-[var(--text-heading)] leading-[1.15] mb-4 md:mb-6" data-id="element-199">
            How to End Mealtime Meltdowns and Expand Your Autistic Child's Diet
            in 15 Minutes a Day
          </h1>
        </motion.div>

        {/* Subtitle */}
        <motion.div variants={item} data-id="element-200">
          <p className="text-lg md:text-2xl text-[var(--accent)] font-serif italic mb-4 md:mb-6" data-id="element-201">
            — Without Tears, Pressure, or Power Struggles.
          </p>
        </motion.div>

        {/* Positioning */}
        <motion.div variants={item} data-id="element-202">
          <p className="text-sm md:text-lg text-[var(--text-primary)] leading-relaxed max-w-2xl mx-auto mb-8 md:mb-12" data-id="element-203">
            The Step-by-Step <strong data-id="element-204">"Texture Hacking"</strong> Method designed
            specifically for Sensory Processing Disorder. Stop worrying about
            malnutrition and start enjoying family dinners again.
          </p>
        </motion.div>

        {/* Book Image */}
        <motion.div variants={item} className="mb-8 md:mb-12 relative" data-id="element-205">
          {/* Pulsing glow behind book */}
          <motion.div className="absolute inset-0 bg-[var(--accent-glow)] blur-[60px] rounded-full transform scale-90" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [0.85, 1.0, 0.85]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-206" />

          {/* Secondary warm glow ring */}
          <motion.div className="absolute inset-[-20px] rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, var(--accent-soft) 0%, transparent 70%)'
        }} animate={{
          opacity: [0.2, 0.5, 0.2],
          scale: [0.9, 1.05, 0.9]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} data-id="element-207" />

          {/* Floating book with shimmer */}
          <motion.div className="relative z-10" animate={{
          y: [0, -12, 0],
          rotateZ: [-0.5, 0.5, -0.5]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-208">
            <div className="relative overflow-hidden rounded-lg" data-id="element-209">
              <img src="https://cdn.magicpatterns.com/uploads/xdkdFJBsvzWQikawQ8nDi2/heylloyd-image-1772934658127.png" alt="Sensory Feeding Victory Guide" className="w-56 md:w-80 mx-auto rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-[var(--border-subtle)]" data-id="element-210" />
              {/* Shimmer shine sweep */}
              <motion.div className="absolute inset-0 rounded-lg pointer-events-none" style={{
              background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.25) 55%, transparent 60%)'
            }} animate={{
              x: ['-150%', '150%']
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              repeatDelay: 4,
              ease: 'easeInOut'
            }} data-id="element-211" />
            </div>

            {/* Floating shadow that responds to movement */}
            <motion.div className="mx-auto mt-4 rounded-full bg-black/10 blur-xl" style={{
            width: '60%',
            height: '12px'
          }} animate={{
            scaleX: [1, 0.85, 1],
            opacity: [0.3, 0.15, 0.3]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-212" />
          </motion.div>
        </motion.div>

        {/* Price Display */}
        <motion.div variants={item} className="mb-4 md:mb-6 flex flex-col items-center" data-id="element-213">
          <div className="flex items-baseline justify-center gap-3" data-id="element-214">
            <span className="text-xl md:text-2xl text-[var(--text-muted)] line-through decoration-[var(--red-emphasis)]/60 opacity-70" data-id="element-215">
              $47
            </span>
            <span className="text-4xl md:text-6xl font-serif font-bold text-[var(--text-heading)]" data-id="element-216">
              $6.99
            </span>
          </div>
          <p className="text-[var(--red-emphasis)] text-xs font-bold uppercase tracking-widest mt-2 bg-[var(--red-emphasis)]/10 px-2 py-0.5 rounded" data-id="element-217">
            85% OFF Today Only
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div variants={item} className="w-full sm:w-auto mb-4 md:mb-6" data-id="element-218">
          <motion.button onClick={scrollToCTA} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="w-full sm:w-auto px-8 md:px-12 py-4 md:py-5 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-bold text-base md:text-lg rounded-xl shadow-lg flex items-center justify-center gap-2 whitespace-nowrap transition-colors" data-id="element-219">
            <Lock className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" data-id="element-220" />
            <span className="uppercase tracking-wide" data-id="element-221">
              YES! I WANT SENSORY VICTORY — $6.99
            </span>
          </motion.button>
        </motion.div>

        {/* Social Proof */}
        <motion.div variants={item} className="mb-4 md:mb-8" data-id="element-222">
          <div className="flex flex-col items-center gap-1.5" data-id="element-223">
            <div className="flex items-center gap-1" data-id="element-224">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" data-id="element-225" />)}
            </div>
            <p className="text-sm text-[var(--text-primary)] font-medium" data-id="element-226">
              Trusted by 2,400+ parents of autistic children
            </p>
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div variants={item} data-id="element-227">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 text-xs text-[var(--text-muted)]" data-id="element-228">
            <span className="flex items-center gap-1.5" data-id="element-229">
              <Download className="w-4 h-4 text-[var(--accent)]" data-id="element-230" />
              Instant Digital Access
            </span>
            <span className="hidden md:inline opacity-30" data-id="element-231">·</span>
            <span className="flex items-center gap-1.5" data-id="element-232">
              <ShieldCheck className="w-4 h-4 text-[var(--accent)]" data-id="element-233" />
              30-Day Guarantee
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>;
}